import styled from '@emotion/styled';

/**
 * Type guard used to check whether a nullable focal point coordinate has a usable value.
 *
 * @param value The focal point coordinate to check.
 * @returns `true` when `value` is a number, meaning it is neither `null` nor `undefined`.
 */
const hasFocalValue = (value: number | null | undefined): value is number => value !== null && value !== undefined;

/** Describes a single responsive image source with URL and width descriptor. */
export interface ImageSource {
    /** The URL of the image at this size. */
    url: string;
    /** The width of the image in pixels (used as `w` descriptor in srcset). */
    width: number;
}

/**
 * The props for the `ResponsiveImage` component.
 * Configures a responsive image with srcset, focal point support, and object-fit.
 */
interface ComponentProps {
    /** Alt text for the image. */
    alt: string;
    /** Focal point X coordinate (0–100). Used for object-position when objectFit is cover. */
    focalX?: number | null;
    /** Focal point Y coordinate (0–100). Used for object-position when objectFit is cover. */
    focalY?: number | null;
    /** CSS height. Defaults to `'100%'`. */
    height?: string;
    /** CSS object-fit value. Defaults to `'cover'`. */
    objectFit?: 'contain' | 'cover' | 'fill' | 'none';
    /**
     * Marks this image as the LCP candidate, applying `fetchpriority="high"` so the browser's
     * preload scanner requests it at high priority instead of the default `"auto"`.
     *
     * @default false
     */
    priority?: boolean;
    /** The `sizes` attribute for responsive image selection. */
    sizes?: string;
    /** Fallback image URL (used as `src`). */
    src: string;
    /** Array of image sources for building the `srcset` attribute. */
    srcSet?: ImageSource[];
    /**
     * The `testId` property represents a unique identifier assigned to a component for testing purposes.
     * It is applied as `data-cy` attribute to enable reliable targeting in Cypress tests.
     *
     * @default 'ResponsiveImage'
     */
    testId?: string;
    /** CSS width. Defaults to `'100%'`. */
    width?: string;
}

/**
 * Renders a responsive `<img>` element with `srcset` and `sizes` attributes for optimal
 * image loading. When `objectFit` is `'cover'`, the focal point (`focalX`/`focalY`) is
 * applied as `object-position` to ensure the subject stays visible during cropping.
 *
 * @param props           The component props.
 * @param props.alt       Alt text for accessibility.
 * @param props.focalX    Focal point X (0–100). Applied when objectFit is cover.
 * @param props.focalY    Focal point Y (0–100). Applied when objectFit is cover.
 * @param props.height    CSS height of the image container. Defaults to `'100%'`.
 * @param props.objectFit CSS object-fit value. Defaults to `'cover'`.
 * @param props.priority  Marks this as the LCP candidate via `fetchpriority="high"`. Defaults to `false`.
 * @param props.sizes     The sizes attribute for the browser to select the appropriate source.
 * @param props.src       Fallback image URL.
 * @param props.srcSet    Array of image sources with URL and width.
 * @param props.testId    An optional test ID used for automation selectors. Defaults to `'ResponsiveImage'`.
 * @param props.width     CSS width of the image container. Defaults to `'100%'`.
 * @returns A JSX element rendering a responsive image.
 *
 * @example
 * ```tsx
 * <ResponsiveImage
 *     alt="Walk cover"
 *     src="/media/hero.webp"
 *     srcSet={[
 *         {url: '/media/card.webp', width: 600},
 *         {url: '/media/hero.webp', width: 1200},
 *     ]}
 *     sizes="100vw"
 *     focalX={50}
 *     focalY={30}
 * />
 * ```
 */
const ResponsiveImage = ({
    alt,
    focalX,
    focalY,
    height = '100%',
    objectFit = 'cover',
    priority = false,
    sizes = '100vw',
    src,
    srcSet,
    testId = 'ResponsiveImage',
    width = '100%'
}: ComponentProps) => (
    <StyledImage
        $height={height}
        $objectFit={objectFit}
        $objectPosition={
            objectFit === 'cover' && hasFocalValue(focalX) && hasFocalValue(focalY)
                ? `${focalX}% ${focalY}%`
                : undefined
        }
        $width={width}
        alt={alt}
        data-cy={testId}
        fetchPriority={priority ? 'high' : undefined}
        sizes={srcSet && srcSet.length > 0 ? sizes : undefined}
        src={src}
        srcSet={srcSet && srcSet.length > 0 ? srcSet.map(s => `${s.url} ${s.width}w`).join(', ') : undefined}
    />
);

ResponsiveImage.displayName = 'ResponsiveImage';

export {ResponsiveImage};

// ── Styled ──────────────────────────────────────────────────────────────

interface StyledImageProps {
    $height: string;
    $objectFit: string;
    $objectPosition?: string;
    $width: string;
}

const StyledImage = styled.img<StyledImageProps>`
    display: block;
    height: ${({$height}) => $height};
    object-fit: ${({$objectFit}) => $objectFit};
    object-position: ${({$objectPosition}) => $objectPosition ?? 'center center'};
    width: ${({$width}) => $width};
`;